import { InputActionBar } from 'components/InputActionBar/InputActionBar';
import { observer } from 'mobx-react-lite';
import { useUnsafePredictionStore } from 'modules/Predictions/contexts/MstPredictionStoreCtx';
import { useSetInputOnMount } from 'modules/Predictions_deprecated/hooks';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { withGuaranteedPredStore } from 'modules/Predictions/withGuaranteedPredStore';
import { IModelPredictionStoreMST } from 'modules/Predictions/mst/ModelPredictionStore';
import { inputActionBarCss } from 'modules/Predictions/components/PredictionMedia/styles';
import { UploadAction } from 'modules/Predictions/components/PredictionSidebar/UploadAction';
import type { EditableModel } from 'modules/Models/components/ModelViewer/useEditableModel';
import { CachedPostModelPredictBody } from 'modules/Predictions/effects';

export const PredictionMediaList: React.FC<{
  editableEntity: CF.UI.EditableEntity | EditableModel;
  disableRouterUpdates?: boolean;
}> = withGuaranteedPredStore(
  observer(({ editableEntity, disableRouterUpdates = false }) => {
    const router = useRouter();
    const store = useUnsafePredictionStore<CF.API.Models.Model, IModelPredictionStoreMST>();
    const {
      media: { inputList, currentInputId, setCurrentInput },
    } = store;
    const isLoggedIn = store.viewState.isLoggedIn;

    const onSelectInput = useCallback(
      (input: CF.API.Inputs.Input) => {
        setCurrentInput(input.id);
        if (!disableRouterUpdates) {
          router.replace(
            {
              query: {
                ...router.query,
                inputId: input.id,
              },
            },
            undefined,
            { shallow: true },
          );
        }

        store.predict({
          ...store?.media?.currentInput?.data,
          ...(store.entity.type === 'model' && { cacheKey: store.media.currentInputId }),
        } as CachedPostModelPredictBody);
      },
      [inputList, setCurrentInput, disableRouterUpdates],
    );

    useSetInputOnMount(setCurrentInput, [store]);

    const TryInputAction: { key: string; element: React.FC; omitIf?: boolean } = {
      key: 'try button',
      element: () => (
        <UploadAction
          isLoggedIn={isLoggedIn}
          store={store}
          editableEntity={editableEntity}
          modelTypeForUi={store.renderingCategory}
          trainedVersionsExist={store.hasTrained}
          pageName="model_page"
        />
      ),
    };

    const actions = [TryInputAction].reduce<{ key: string; element: JSX.Element }[]>(
      (acc, { key, element: Element, omitIf }) => (omitIf ? acc : [...acc, { key, element: <Element /> }]),
      [] as { key: string; element: JSX.Element }[],
    );

    return (
      <InputActionBar
        topActions={actions}
        className={inputActionBarCss}
        currentInputId={currentInputId}
        inputs={inputList}
        setCurrentInput={onSelectInput}
        altPrefix={(editableEntity.data as unknown as { name?: string }).name}
      />
    );
  }),
);
